import { useAerBilan } from './useAerBilan';
import { useNotification } from '../../../utils/notificationHelpers';

export const useDsnActions = () => {
  const {
    fetchAerBilan,
    downloadMultipleAerBilans,
    loading: aerBilanLoading,
    downloadLoading,
  } = useAerBilan();

  const notification = useNotification();
  const { displayNotification } = notification;

  const handleViewDeclaration = async (instanceId: string, canDisplayPdf: boolean) => {
    if (!canDisplayPdf) {
      displayNotification('Cette déclaration ne peut pas être visualisée', 'error');
      return;
    }

    try {
      const result = await fetchAerBilan(instanceId);

      if (result.success) {
        if (result.data?.pdfUrl) {
          const pdfUrl = result.data.pdfUrl;
          const newWindow = window.open(pdfUrl, '_blank');

          if (newWindow) {
            newWindow.focus();
          } else {
            displayNotification(
              "Le navigateur a bloqué l'ouverture du PDF. Veuillez autoriser les popups.",
              'error',
            );
            return;
          }
        }

        displayNotification('Déclaration récupérée avec succès', 'success');
      } else {
        displayNotification('Impossible de récupérer les détails de la déclaration', 'error');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Une erreur est survenue lors de la récupération des détails';
      displayNotification(errorMessage, 'error');
    }
  };

  const handleDownload = async (instanceId: string, canDisplayZip: boolean) => {
    if (!canDisplayZip) {
      displayNotification('Cette déclaration ne peut pas être téléchargée', 'error');
      return;
    }

    try {
      const result = await downloadMultipleAerBilans([instanceId]);

      if (result.success) {
        displayNotification('Déclaration téléchargée avec succès', 'success');
      } else {
        displayNotification(result.error ?? 'Impossible de télécharger la déclaration', 'error');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Une erreur est survenue lors du téléchargement';
      displayNotification(errorMessage, 'error');
    }
  };

  const handleMultipleDownload = async (selectedInstanceIds: string[]) => {
    if (selectedInstanceIds.length === 0) {
      displayNotification('Veuillez sélectionner au moins un bilan téléchargeable', 'error');
      return;
    }

    try {
      const result = await downloadMultipleAerBilans(selectedInstanceIds);

      if (result.success) {
        displayNotification(
          selectedInstanceIds.length > 1
            ? 'Les bilans ont été téléchargés avec succès'
            : 'Le bilan a été téléchargé avec succès',
          'success',
        );
      } else {
        displayNotification(
          result.error || 'Une erreur est survenue lors du téléchargement',
          'error',
        );
      }
    } catch (error) {
      displayNotification(
        error instanceof Error ? error.message : 'Une erreur est survenue lors du téléchargement',
        'error',
      );
    }
  };

  return {
    handleViewDeclaration,
    handleDownload,
    handleMultipleDownload,
    aerBilanLoading,
    downloadLoading,
    notification,
  };
};
